import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import axios from "../../plugins/axios";
import { format } from "date-fns";
import {
    Box,
    TextField,
    Typography,
    Table,
    TableCell,
    TableBody,
    TableHead,
    TableRow,
    TableFooter,
    TablePagination,
    Paper,
    FormControlLabel,
    Switch,
    CircularProgress,
    Tooltip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import AccountAutoComplete from '../AccountAutoComplete';
import { makeStyles } from '@mui/styles';
import TimesheetEntryDialog from "./TimesheetEntryDialog";
import {
    PostAdd,
    Check,
    Add,
    ArrowRight,
    ArrowDropDown,
    SubdirectoryArrowRight,
} from "@mui/icons-material";

const useStyles = makeStyles(theme => ({
    spinnerContainer: {
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center'
    },
    clamped: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: "-webkit-box",
        "-webkit-line-clamp": 3,
        "-webkit-box-orient": "vertical",
        fontSize: '0.9em',
        whiteSpace: 'pre-wrap'
    }
}))

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

const AdviceEntries = ({ calcDuration, employeeId, activityTypes, advisoryTypes, filesTooltip }) => {

    const classes = useStyles();

    const [showAllAdvice, setShowAllAdvice] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [accountId, setAccountId] = useState();
    const [loading, setLoading] = useState(false);
    const [timeEntries, setTimeEntries] = useState([]);
    const [dialogEntry, setDialogEntry] = useState();
    const [entryCount, setEntryCount] = useState(0);
    const [page, setPage] = useState(0);
    const pageSize = 5;
    const [expandedIds, setExpandedIds] = useState([]);
    const [entryToPublish, setEntryToPublish] = useState();
    const [publishingId, setPublishingId] = useState();

    const handleAccountChange = (v) => {
        console.log("handleAccountChange", v);
        setAccountId(v && v.value);
    };

    useEffect(() => { console.log({ accountId }); }, [accountId]);

    useEffect(() => {
        setPage(0);
    }, [accountId, fromDate, toDate, showAllAdvice, employeeId]);

    const [criteria, setCriteria] = useState({
        fromDate,
        toDate,
        accountId,
        employeeId,
    });

    useEffect(() => {
        let newCriteria = {
            fromDate,
            toDate,
            accountId,
            employeeId: (accountId && showAllAdvice) ? null : employeeId
        };

        if (JSON.stringify(newCriteria) !== JSON.stringify(criteria)) {
            setCriteria(newCriteria);
            setPage(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, employeeId, fromDate, showAllAdvice, toDate]);

    const getTimeEntries = useCallback(async () => {
        try {
            setLoading(true);

            var response = await axios.post(`${apiUrl}/advicedata`, { ...criteria, pageNumber: page + 1, pageSize });
            var entries = response.data.entries || [];
            setEntryCount(response.data.entryCount);

            setTimeEntries(entries);
        } finally {
            setLoading(false);
        }
    }, [criteria, page]);

    const onSubmitted = () => {
        setDialogEntry();
        getTimeEntries();
    };

    useEffect(() => {
        getTimeEntries();
    }, [getTimeEntries]);

    const addToSafetyNest = async (entry, confirmed) => {
        if (confirmed || entry.children.length === 0) {
            setPublishingId(entry.id);
            await axios.post(`${apiUrl}/publishtimesheetentry`, entry);
            setEntryToPublish();
            setTimeEntries(timeEntries.map(te => te.id === entry.id ? { ...te, publishToSafetyNest: true } : te));
            setPublishingId();
        } else {
            setEntryToPublish(entry);
        }
    };

    const addChild = (entry) => {
        setDialogEntry({
            ...entry,
            description: null,
            startTime: null,
            endTime: null,
        });
    };

    const toggleExpanded = (id) => {
        if (expandedIds.includes(id))
            setExpandedIds(expandedIds.filter(x => x !== id));
        else
            setExpandedIds([...expandedIds, id]);
    }

    const EntryRow = ({ entry, isChild, isLastChild }) => {

        let hideBorder = (!isChild && expandedIds.includes(entry.id)) || (isChild && !isLastChild);
        return (
            <TableRow sx={{
                ...(hideBorder && {
                    [`& .${tableCellClasses.root}`]: {
                        borderBottom: "none"
                    }
                })
            }}>
                <TableCell style={{ width: '24px', cursor: entry.children.length > 0 ? "pointer" : null }} onClick={() => toggleExpanded(entry.id)}>
                    {entry.children.length > 0 && (expandedIds.includes(entry.id) ? <ArrowDropDown /> : <ArrowRight />)}
                </TableCell>
                <TableCell>
                    <Box display="flex" alignItems="center">
                        {isChild && <SubdirectoryArrowRight style={{ fontSize: '0.8em' }} />}
                        <span>{format(new Date(entry.startTime), "dd/MM/yy")}</span>
                    </Box>
                </TableCell>
                <TableCell><a href={entry.url} target="_blank" rel="noreferrer">{entry.name}</a></TableCell>
                <TableCell>{entry.consultantName}</TableCell>
                <TableCell>{entry.accountName}</TableCell>
                <TableCell><Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{entry.description}</div>}><div className={classes.clamped}>{entry.description}</div></Tooltip></TableCell>
                <TableCell>{entry.advisoryType}</TableCell>
                <TableCell>{entry.contactName}</TableCell>
                <TableCell align="center">{format(new Date(entry.startTime), "HH:mm")}</TableCell>
                <TableCell align="center">{format(new Date(entry.endTime), "HH:mm")}</TableCell>
                <TableCell align="center">{filesTooltip(entry.filenames)}</TableCell>
                <TableCell align="center">
                    {!isChild &&
                        (entry.publishToSafetyNest
                            ? <Check style={{ color: 'green' }} />
                            : <Button size="small" variant="contained" disabled={publishingId === entry.id} onClick={() => addToSafetyNest(entry)}>{publishingId === entry.id ? "Linking.." : "Link"}</Button>
                        )
                    }
                </TableCell>
                <TableCell>{!isChild && <IconButton onClick={() => addChild(entry)}><Add /></IconButton>}</TableCell>
            </TableRow>
        )
    };

    return (
        <Box display="flex" flexDirection="column" style={{ paddingTop: "10px" }} gap="10px">
            <Box display="flex" alignItems="center" gap="10px">
                <Typography variant="h6">Advice Entries</Typography>
                <Box flexGrow={1}></Box>
                {!!accountId &&
                    <FormControlLabel
                        control={<Switch checked={showAllAdvice} onChange={(e) => setShowAllAdvice(e.target.checked)} />}
                        label="Show All Advice"
                        labelPlacement="start"
                    />
                }
                <DatePicker
                    label="Date From"
                    inputFormat="dd/MM/yyyy"
                    slotProps={{
                        textField: {
                            size: 'small',                            
                        },
                    }}
                    value={fromDate}
                    onChange={setFromDate}
                    maxDate={toDate}
                    inputProps={{ style: { textAlign: "center", width: "100px" } }}
                    renderInput={(params) => <TextField {...params} size="small" onKeyDown={(e) => e.preventDefault()} name="noAutoFill" />}
                    componentsProps={{
                        actionBar: {
                            actions: ['clear'],
                        },
                    }}
                />
                <DatePicker
                    inputFormat="dd/MM/yyyy"
                    label="Date To"
                    value={toDate}
                    slotProps={{
                        textField: {
                            size: 'small',
                        },
                    }}
                    onChange={setToDate}
                    minDate={fromDate}
                    inputProps={{ style: { textAlign: "center", width: "100px" } }}
                    renderInput={(params) => <TextField {...params} size="small" onKeyDown={(e) => e.preventDefault()} name="noAutoFill" />}
                    componentsProps={{
                        actionBar: {
                            actions: ['clear'],
                        },
                    }}
                />
                <Box style={{ width: '300px' }}>
                    <AccountAutoComplete handleAccountChange={handleAccountChange} label="Account Filter" />
                </Box>
            </Box>
            {loading &&
                <div className={classes.spinnerContainer}>
                    <CircularProgress />
                </div>
            }
            {!loading && timeEntries.length === 0 &&
                <Paper>
                    <Box display="flex" flexDirection="column" alignItems="center" gap="5px" p={4}>
                        <Box style={{ borderRadius: '50%', background: '#e5deff', padding: '10px', lineHeight: '1em' }} >
                            <PostAdd style={{ width: '30px', height: '30px' }} />
                        </Box>
                        <Typography variant="h6">No Entries for selected criteria</Typography>
                    </Box>
                </Paper>
            }
            {!loading && timeEntries.length > 0 &&
                <Paper>
                    <Table size="small" >
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Id</TableCell>
                                <TableCell>Consultant</TableCell>
                                <TableCell>Account</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Advisory Type</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell align="center">Start</TableCell>
                                <TableCell align="center">End</TableCell>
                                <TableCell align="center">Files</TableCell>
                                <TableCell align="center">SafetyNest</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {timeEntries.map((entry) => (
                                <React.Fragment key={entry.id}>
                                    <EntryRow entry={entry} />
                                    {expandedIds.includes(entry.id) && entry.children.map((e, index) => <EntryRow key={e.id} entry={e} isChild isLastChild={index === entry.children.length - 1} />)}
                                </React.Fragment>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[]}
                                    count={entryCount}
                                    rowsPerPage={pageSize}
                                    page={page}
                                    onPageChange={(e, newPage) => setPage(newPage)}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </Paper>
            }
            {dialogEntry &&
                <TimesheetEntryDialog
                    entry={dialogEntry}
                    setEntry={setDialogEntry}
                    date={new Date()}
                    activityTypes={activityTypes}
                    advisoryTypes={advisoryTypes}
                    calcDuration={calcDuration}
                    onSubmitted={onSubmitted}
                />
            }
            {entryToPublish &&
                <Dialog
                    open
                >
                    <DialogTitle>Publish Advice</DialogTitle>
                    <DialogContent>
                        <p>Publishing {entryToPublish.name} will also display {entryToPublish.children.length} child entries in SafetyNest</p>
                        <p>Are you sure you want to publish?</p>
                    </DialogContent>
                    <DialogActions>
                        <Button disabled={!!publishingId} onClick={() => setEntryToPublish()}>Cancel</Button>
                        <Button disabled={!!publishingId} onClick={() => addToSafetyNest(entryToPublish, true)} color='primary'>{publishingId ? "Publishing.." : "Publish"}</Button>
                    </DialogActions>
                </Dialog>
            }
        </Box>
    );
};

export default AdviceEntries;
